import { useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

/**
 * A custom hook to manage query parameters in the URL.
 * @returns - Methods to add, remove, and set query parameters.
 */
export function useQueryParams() {
  const router = useRouter();
  const searchParams = useSearchParams();

  /**
   * Adds or updates a query parameter in the URL.
   * @param key - The key of the query parameter.
   * @param value - The value of the query parameter.
   */
  const addQueryParam = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(
        searchParams ? Array.from(searchParams.entries()) : {},
      );
      params.set(key, value);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  /**
   * Removes a query parameter from the URL.
   * @param key - The key of the query parameter to remove.
   */
  const removeQueryParam = useCallback(
    async (key: string) => {
      if (searchParams) {
        const params = new URLSearchParams(searchParams);
        params.delete(key);
        const paramsString = params.toString();
        router.replace(`${paramsString ? '?' : ''}${paramsString}`, {
          scroll: false,
        });
      }
    },
    [router, searchParams],
  );

  /**
   * Replaces the query parameters with the provided object.
   * @param newParams - An object representing the new query parameters.
   */
  const setQueryParams = useCallback(
    (newParams: Record<string, string>) => {
      const params = new URLSearchParams(newParams);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router],
  );

  /**
   * Set or remove query params based on values in provided object
   * (undefined/null values removed).
   * @param paramsUpdates - An object representing the query parameters updates
   */
  const updateQueryParams = useCallback(
    (paramsUpdates: Record<string, string | undefined | null>) => {
      const params = new URLSearchParams(searchParams ?? {});
      Object.entries(paramsUpdates).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  return { addQueryParam, removeQueryParam, setQueryParams, updateQueryParams };
}
